import { Title } from "../components/Typography/Typography";
import styled from "styled-components/macro";
import { screenSize } from "../theme";

interface PageWrapperProps {
  narrow?: boolean;
  fullWidth?: boolean;
}

export const PageWrapper = styled.div<PageWrapperProps>`
  width: 100%;
  min-width: 300px;
  margin: 0 auto;
  .ql-editor {
    height: auto !important;
  }
  .__react_component_tooltip.place-top::before {
    bottom: -1px;
    height: 8px !important;
  }
`;

/**
 * Typically used in conjuction with a search bar on the left hand side.
 */
export const HorizontalButtonsContainer = styled.div`
  display: flex;
  gap: 18px;
  flex-wrap: wrap;
  height: 37.5px;
  justify-content: flex-end;
  margin-left: auto;
  @media ${screenSize.medium} {
    justify-content: flex-start;
    margin-right: auto;
    margin-left: 0;
    position: absolute;
    right: 0;
    bottom: 40px;
  }
`;

export const PageHeader = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: flex-start;
  position: relative;
  & > *[data-alignright] {
    margin-left: auto;
  }
  @media ${screenSize.medium} {
    flex-wrap: wrap;
    .searchBar {
      flex: 1 1 100%;
      order: 10;
      max-width: 100%;
    }
  }
`;

export const PageTitle = styled(Title)`
  display: inline-block;
  margin: 10px;
  margin-left: 0;
  line-height: ${({ theme }) => theme.resolve("line_height_3xl")};
  letter-spacing: -0.5px;
`;

export const HeaderBar = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: flex-start;
  position: relative;
  margin: ${({ theme }) => theme.resolve("spacing_none")} -8px 5px 0;
  flex-wrap: wrap;
  & > * {
    margin-right: ${({ theme }) => theme.resolve("spacing_md")};
    margin-bottom: 10px;
  }
  & > *[data-alignright] {
    margin-left: auto;
  }

  @media ${screenSize.medium} {
    margin: 10px 0 5px;
    & > * {
      margin-right: 5px;
      margin-bottom: ${({ theme }) => theme.resolve("spacing_md")};
    }
    & > *[data-alignright] {
      margin-left: 0 !important;
    }
  }
`;

export const FiltersWrapper = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: flex-start;
  position: relative;
  margin: 10px -8px 5px ${({ theme }) => theme.resolve("spacing_none")};
  flex-wrap: wrap;
  width: calc(100% - 150px);
  & > * {
    margin-right: ${({ theme }) => theme.resolve("spacing_md")};
    margin-bottom: 10px;
  }
  & > *[data-alignright] {
    margin-left: auto;
  }

  @media ${screenSize.mLarge} {
    width: calc(100% - 205px);
  }

  @media ${screenSize.medium} {
    margin: 10px 0 5px;
    width: 100%;
    flex-wrap: nowrap;
    overflow-x: scroll;
    & > * {
      margin-right: 5px;
      margin-bottom: ${({ theme }) => theme.resolve("spacing_md")};
    }
    & > .rightAlign {
      margin-left: 0 !important;
    }
  }
`;

export const ContentWrapper = styled.div`
  width: 100%;
`;

export const TwoColumnLayout = styled.div`
  width: 100%;
  display: Flex;
  flex-direction: row;
  @media ${screenSize.medium} {
    flex-direction: column;
  }
`;

export const TwoColumnOrderTotalSection = styled.div`
  display: flex;
  padding: ${({ theme }) => theme.resolve("spacing_2xl")} 0;
  justify-content: space-between;
  align-items: stretch;
  @media ${screenSize.small} {
    flex-direction: column-reverse;
  }
`;

type CardProps = {
  selectedBorder?: boolean;
};

/**
 * TODO: remove CardNoMargin when the margin can be removed from this component.
 */
export const Card = styled.div<CardProps>`
  border-radius: ${({ theme }) => theme.resolve("radius_xs")};
  border: ${({ theme, selectedBorder }) => `${
    selectedBorder ? theme.resolve("border_2xl") : theme.resolve("border_md")
  } solid
    ${selectedBorder ? theme.tertiaryBorder : theme.secondaryBorder}`};
  background-color: ${(props) => props.theme.secondaryBG};
  color: #0f1d2b;
  font-size: 13px;
  padding: ${({ theme }) => theme.resolve("spacing_2xl")};
  width: 100%;
  margin: ${({ theme }) => theme.resolve("spacing_2xl")} 0;
`;

export const BorderCard = styled.div`
  border: ${({ theme }) => theme.resolve("border_md")} solid
    ${({ theme }) => theme.secondaryBorder};
  width: 100%;
  padding: ${({ theme }) => theme.resolve("spacing_2xl")};
  margin-bottom: ${({ theme }) => theme.resolve("spacing_2xl")};
  border-radius: ${({ theme }) => theme.resolve("radius_md")};
`;

export const WhiteInnerCard = styled.div`
  background-color: #fff;
  border: ${({ theme }) => theme.resolve("border_md")} solid
    ${({ theme }) => theme.resolve("color_border_lighter")};
  width: 100%;
  padding: ${({ theme }) => theme.resolve("spacing_2xl")};
  margin-bottom: 15px;
  border-radius: ${({ theme }) => theme.resolve("radius_md")};
`;

export const SettingsCard = styled(Card)`
  padding-left: 56px;
  @media ${screenSize.small} {
    padding-left: 15px;
  }
`;

export const FlexRow = styled.div<{ rowItems: number; itemMargin: string }>`
  display: flex;
  flex-wrap: wrap;
  & > * {
    flex-grow: 1;
    width: ${(props) => (props.rowItems ? 100 / props.rowItems : 100)}%;
    margin: ${(props) => (props.itemMargin ? props.itemMargin : 0)};
  }
`;

export const TwoColumGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  grid-template-rows: 1fr;
  grid-column-gap: 30px;
  grid-row-gap: ${({ theme }) => theme.resolve("spacing_none")};
  @media ${screenSize.small} {
    grid-template-columns: 1fr;
  }
`;

interface IHorizontalSeparator {
  margin?: string;
}
export const FullWidthHorizontalSeparator = styled.div<IHorizontalSeparator>`
  border-bottom: ${({ theme }) => theme.resolve("border_md")} solid
    ${({ theme }) => theme.primaryBorder};
  left: 0;
  right: 0;
  position: absolute;
  margin: ${({ margin }) => margin ?? 0};
`;
